import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, Send, ChevronDown, ArrowLeft, MapPin } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CallButton from "../components/CallButton";
import { Heart, Star, Squiggle, Sun, Cloud } from "../components/Illustrations";
import { servicePages } from "../data/servicePages";
import { rooms } from "../data/rooms";

function useSeo(page) {
  useEffect(() => {
    if (!page) return;
    document.title = page.seoTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", page.metaDescription);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `https://orizontesanaptixis.gr/${page.slug}`);
    const setProp = (prop, val) => {
      const el = document.querySelector(`meta[property="${prop}"]`);
      if (el) el.setAttribute("content", val);
    };
    const pageUrl = `https://orizontesanaptixis.gr/${page.slug}`;
    setProp("og:title", page.seoTitle);
    setProp("og:description", page.metaDescription);
    setProp("og:url", pageUrl);
    setProp("twitter:title", page.seoTitle);
    setProp("twitter:description", page.metaDescription);
    setProp("twitter:url", pageUrl);

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const localSchema = {
      "@context": "https://schema.org",
      "@type": ["LocalBusiness", "MedicalBusiness"],
      name: "Ορίζοντες Ανάπτυξης",
      url: `https://orizontesanaptixis.gr/${page.slug}`,
      telephone: "+302105719375",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Υψούντος 7",
        addressLocality: "Περιστέρι",
        addressRegion: "Αττική",
        postalCode: "12131",
        addressCountry: "GR",
      },
      geo: { "@type": "GeoCoordinates", latitude: 38.0151, longitude: 23.692 },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "13:00",
          closes: "21:00",
        },
      ],
      areaServed: [
        { "@type": "City", name: "Περιστέρι" },
        { "@type": "AdministrativeArea", name: "Δυτικά Προάστια Αθήνας" },
      ],
      makesOffer: {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: page.name,
          url: `https://orizontesanaptixis.gr/${page.slug}`,
        },
      },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);
    const script2 = document.createElement("script");
    script2.type = "application/ld+json";
    script2.textContent = JSON.stringify(localSchema);
    document.head.appendChild(script2);
    window.scrollTo(0, 0);
    return () => {
      document.head.removeChild(script);
      document.head.removeChild(script2);
      document.title = "Ορίζοντες Ανάπτυξης | Κέντρο Ειδικών Θεραπειών στο Περιστέρι";
      if (canonical) canonical.setAttribute("href", "https://orizontesanaptixis.gr/");
      if (desc)
        desc.setAttribute(
          "content",
          "Κέντρο Ειδικών Θεραπειών «Ορίζοντες Ανάπτυξης» στο Περιστέρι. Λογοθεραπεία, Εργοθεραπεία, Ψυχολογική Υποστήριξη, Πρώιμη Παρέμβαση & Συμβουλευτική Γονέων. Δωρεάν αξιολόγηση για κάθε παιδί."
        );
      const defaultTitle = "Ορίζοντες Ανάπτυξης | Κέντρο Ειδικών Θεραπειών στο Περιστέρι";
      const defaultDesc =
        "Λογοθεραπεία, Εργοθεραπεία, Ψυχολογική Υποστήριξη, Πρώιμη Παρέμβαση & Συμβουλευτική Γονέων. Δωρεάν αξιολόγηση για κάθε παιδί.";
      setProp("og:title", defaultTitle);
      setProp("og:description", defaultDesc);
      setProp("og:url", "https://orizontesanaptixis.gr/");
      setProp("twitter:title", defaultTitle);
      setProp("twitter:description", defaultDesc);
      setProp("twitter:url", "https://orizontesanaptixis.gr/");
    };
  }, [page]);
}

const sectionBgs = ["bg-white", "bg-brand-cream"];

export default function ServicePage() {
  const { slug } = useParams();
  const page = servicePages.find((p) => p.slug === slug);
  useSeo(page);

  if (!page) return <Navigate to="/" replace />;

  const others = servicePages.filter((p) => p.slug !== slug);

  return (
    <div data-testid={`service-page-${page.slug}`} className="min-h-screen bg-brand-bg">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-16 pb-20 overflow-hidden">
          <Sun className="absolute top-10 right-16 animate-spin-slow opacity-90 hidden md:block" size={80} />
          <Cloud className="absolute top-24 left-10 hidden lg:block" size={80} />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/"
              data-testid="service-back-link"
              className="inline-flex items-center gap-2 font-body text-sm font-bold text-brand-textSoft hover:text-brand-primary transition-colors mb-8"
            >
              <ArrowLeft size={16} />
              Πίσω στην αρχική
            </Link>
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <div className={`inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white border-2 ${page.borderColor} mb-6 -rotate-1`}>
                  <Heart size={18} color="#F4A899" />
                  <span className="font-body text-sm font-bold text-brand-text">{page.name}</span>
                </div>
                <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-brand-text leading-tight">
                  {page.h1}
                  <br />
                  <span className="font-hand text-4xl sm:text-5xl lg:text-6xl text-brand-primary">
                    {page.handWord}
                  </span>
                </h1>
                <Squiggle className="my-5" width={170} color="#FBE4A8" />
                <p className="font-body text-base sm:text-lg text-brand-textSoft leading-relaxed">
                  {page.intro}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mt-8">
                  <a
                    href="tel:+302105719375"
                    data-testid="service-cta-call"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-heading font-bold px-7 py-3.5 border-3 border-dashed border-white shadow-[0_5px_0_-2px_rgba(91,79,70,0.25)] hover:scale-105 transition-all duration-300"
                  >
                    <Phone size={18} strokeWidth={2.4} />
                    210 571 9375
                  </a>
                  <Link
                    to="/epikoinonia"
                    data-testid="service-cta-form"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-brand-text font-heading font-bold px-7 py-3.5 border-3 border-dashed border-brand-blue hover:scale-105 transition-all duration-300"
                  >
                    <Send size={18} strokeWidth={2.4} />
                    Στείλτε μας μήνυμα
                  </Link>
                </div>
                <p
                  data-testid="service-location-strip"
                  className="inline-flex items-start gap-2 mt-6 font-body text-sm text-brand-textSoft"
                >
                  <MapPin size={16} className="mt-0.5 text-brand-primary flex-shrink-0" />
                  <span>
                    Υψούντος 7, <strong className="text-brand-text">Περιστέρι</strong> (Μετρό Αγ.
                    Αντωνίου) — εξυπηρετούμε όλα τα Δυτικά Προάστια της Αθήνας
                  </span>
                </p>
              </div>
              <div className="relative">
                <Star className="absolute -top-5 -right-2 z-10 animate-wiggle" size={42} color="#FBE4A8" />
                <Heart className="absolute -bottom-5 -left-3 z-10 animate-pulse-soft" size={40} color="#FBC4D0" />
                <div className={`rounded-[2.5rem] overflow-hidden border-4 border-dashed ${page.borderColor} ${page.bgColor} rotate-1 hover:rotate-0 transition-transform duration-500 shadow-[0_12px_0_-3px_rgba(91,79,70,0.15)]`}>
                  <img
                    src={page.image}
                    alt={page.imageAlt}
                    width="1264"
                    height="848"
                    className="w-full h-auto object-cover"
                    loading="eager"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sections */}
        {page.sections.map((s, i) => (
          <section key={i} className={`py-14 ${sectionBgs[i % 2]}`}>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`rounded-[2rem] ${page.bgColor} border-3 border-dashed border-brand-text/15 p-8 md:p-10 ${i % 2 ? "rotate-1" : "-rotate-1"} hover:rotate-0 transition-transform duration-500 shadow-[0_8px_0_-2px_rgba(91,79,70,0.12)]`}>
                <h2 className="font-heading font-bold text-2xl sm:text-3xl text-brand-text leading-tight mb-4">
                  {s.h2}
                </h2>
                <p className="font-body text-brand-textSoft leading-relaxed text-base sm:text-lg">
                  {s.text}
                </p>
                {s.text2 && (
                  <p className="font-body text-brand-textSoft leading-relaxed text-base sm:text-lg mt-4">
                    {s.text2}
                  </p>
                )}
                {s.bullets && (
                  <ul className="mt-5 space-y-3">
                    {s.bullets.map((b, j) => (
                      <li
                        key={j}
                        className="flex items-start gap-3 font-body text-brand-textSoft text-base sm:text-lg leading-relaxed"
                      >
                        <Star size={18} color="#F4A899" className="mt-1.5 flex-shrink-0" />
                        <span>
                          <strong className="text-brand-text">{b.t}</strong> {b.d}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.note && (
                  <p className="mt-5 font-body text-sm text-brand-textSoft italic bg-white/70 rounded-2xl p-4 border-2 border-dashed border-brand-text/10">
                    {s.note}
                  </p>
                )}
              </div>
            </div>
          </section>
        ))}

        {/* Room photos */}
        {page.rooms && (
          <section className="py-14 bg-brand-bg" data-testid="service-rooms-gallery">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8">
                <h2 className="font-heading font-bold text-2xl sm:text-3xl text-brand-text">
                  Ο χώρος μας{" "}
                  <span className="font-hand text-3xl sm:text-4xl text-brand-primary">από κοντά</span>
                </h2>
                <Squiggle className="mx-auto mt-3" width={140} color="#FBE4A8" />
              </div>
              {rooms
                .filter((r) => page.rooms.includes(r.id))
                .map((room) => {
                  const shots = room.photos.slice(0, page.roomPhotoCount || 2);
                  return (
                    <div key={room.id} className="mb-8">
                      <h3 className="font-heading font-bold text-lg text-brand-text mb-3">{room.name}</h3>
                      <div className={shots.length === 1 ? "max-w-md" : "grid grid-cols-2 gap-4"}>
                        {shots.map((src, j) => (
                          <div key={j} className={`rounded-[1.5rem] overflow-hidden border-3 border-dashed ${room.color} bg-white ${j % 2 ? "rotate-1" : "-rotate-1"} hover:rotate-0 transition-transform duration-500 shadow-[0_6px_0_-2px_rgba(91,79,70,0.15)]`}>
                            <img src={src} alt={`${room.name} — φωτογραφία ${j + 1}`} loading="lazy" decoding="async" className="w-full h-44 sm:h-60 object-cover" />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              <div className="text-center">
                <Link to="/oi-choroi-mas" data-testid="service-rooms-link" className="inline-flex items-center gap-2 font-heading font-bold text-brand-primaryHover hover:gap-3 transition-all">
                  Δείτε όλους τους χώρους μας →
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        <section className="py-16 bg-brand-cream" data-testid="service-faq-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-brand-text">
                Συχνές{" "}
                <span className="font-hand text-4xl sm:text-5xl text-brand-primary">ερωτήσεις</span>
              </h2>
              <Squiggle className="mx-auto mt-4" width={150} color="#FBC4D0" />
            </div>
            <div className="space-y-4">
              {page.faq.map((f, i) => (
                <details
                  key={i}
                  data-testid={`service-faq-${i}`}
                  className="group bg-white rounded-2xl border-3 border-dashed border-brand-text/15 p-5 open:border-brand-primary/40 transition-colors"
                >
                  <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-heading font-bold text-brand-text text-base sm:text-lg">
                    {f.q}
                    <ChevronDown size={20} className="flex-shrink-0 text-brand-primary transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="font-body text-brand-textSoft leading-relaxed mt-3 text-sm sm:text-base">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section className="py-16 bg-brand-bg">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-[2.5rem] bg-gradient-to-br from-brand-pinkSoft via-brand-yellowSoft to-brand-blueSoft border-4 border-dashed border-brand-pink p-10 text-center -rotate-1 shadow-[0_10px_0_-3px_rgba(91,79,70,0.15)]">
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-brand-text">
                Κλείστε{" "}
                <span className="font-hand text-3xl sm:text-4xl text-brand-primary">δωρεάν αξιολόγηση</span>
              </h2>
              <p className="font-body text-brand-textSoft mt-3 max-w-xl mx-auto">
                Η πρώτη αξιολόγηση στο κέντρο μας στο Περιστέρι είναι εντελώς δωρεάν. Καλέστε μας ή
                στείλτε μας μήνυμα και θα σας απαντήσουμε άμεσα.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-7">
                <a
                  href="tel:+302105719375"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-heading font-bold px-7 py-3.5 border-3 border-dashed border-white shadow-[0_5px_0_-2px_rgba(91,79,70,0.25)] hover:scale-105 transition-all duration-300"
                >
                  <Phone size={18} strokeWidth={2.4} />
                  Καλέστε μας τώρα
                </a>
                <Link
                  to="/epikoinonia"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-brand-text font-heading font-bold px-7 py-3.5 border-3 border-dashed border-brand-secondary hover:scale-105 transition-all duration-300"
                >
                  <Send size={18} strokeWidth={2.4} />
                  Φόρμα επικοινωνίας
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Internal links */}
        <section className="pb-20 bg-brand-bg" data-testid="service-related-links">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-xl text-brand-text mb-6">
              Δείτε επίσης τις υπηρεσίες μας
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/${o.slug}`}
                  data-testid={`related-link-${o.slug}`}
                  className={`px-5 py-2.5 rounded-full bg-white border-2 border-dashed ${o.borderColor} font-body text-sm font-bold text-brand-text hover:scale-105 hover:bg-brand-cream transition-all duration-300`}
                >
                  {o.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <CallButton />
    </div>
  );
}
